"use client";

import React, { useEffect, useState, useCallback } from "react";
import { Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  quote: "FilmFrame beautifully captures the timeless essence of film photography. A true celebration of analog art.",
  author: "Jane Doe",
  position: "Photographer & Collector"
}, {
  quote: "The detailed insights and high-quality scans make this site my go-to for inspiration and learning.",
  author: "John Smith",
  position: "Creative Director"
}, {
  quote: "An archival masterpiece. The attention to grain structure and exposure details is simply unparalleled in the digital space.",
  author: "Elena Rossi",
  position: "Gallery Curator"
}];
export default function CollectorPerspectives() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [flash, setFlash] = useState(false);
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const triggerFlash = useCallback(() => {
    setFlash(true);
    const timer = setTimeout(() => setFlash(false), 150);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
      triggerFlash();
    });
  }, [api, triggerFlash]);
  const handleDotClick = (index: number) => {
    if (!api) return;
    api.scrollTo(index);
  };
  return <section id="collector-perspectives" ref={ref} className={cn("dark bg-background text-foreground relative w-full overflow-hidden py-24 md:py-32", "transition-opacity duration-1000", isVisible ? "opacity-100" : "opacity-0")}>
      {}
      <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_center,hsl(28_60%_15%/_0.4),transparent_70%)] pointer-events-none" />
      
      {}
      <div className={cn("pointer-events-none absolute inset-0 z-50 bg-white mix-blend-overlay transition-opacity duration-150 ease-out", flash ? "opacity-40" : "opacity-0")} />

      <div className="container relative z-10 mx-auto px-6">
        <div className="mx-auto max-w-4xl">
          <Carousel setApi={setApi} className="w-full">
            <CarouselContent>
              {testimonials.map((item, i) => <CarouselItem key={i} data-index={i}>
                  <div className="flex flex-col items-center text-center">
                    <Quote className="mb-8 h-8 w-8 text-primary/60 opacity-50" />
                    
                    <blockquote className="mb-10 max-w-2xl">
                      <p className="font-serif text-3xl leading-tight italic tracking-tight text-foreground md:text-4xl lg:text-5xl">
                        &ldquo;{item.quote}&rdquo;
                      </p>
                    </blockquote>

                    <div className="flex flex-col items-center gap-1">
                      <cite className="font-mono text-sm font-medium uppercase tracking-widest text-primary not-italic">
                        {item.author}
                      </cite>
                      <span className="font-mono text-xs text-muted-foreground">
                        {item.position}
                      </span>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
          </Carousel>

          {}
          <div className="mt-16 flex justify-center">
            <div className="flex items-center gap-6 border-t border-border/30 pt-6">
              {testimonials.map((_, i) => <button key={i} onClick={() => handleDotClick(i)} className={cn("group relative flex flex-col items-center gap-2 outline-none transition-all duration-300", current === i ? "text-primary" : "text-muted-foreground hover:text-foreground")} aria-label={`Go to slide ${i + 1}`}>
                  <span className="font-mono text-xs font-medium tracking-widest">
                    0{i + 1}
                  </span>
                  {}
                  <span className={cn("h-1 w-1 rounded-full transition-all duration-300", current === i ? "bg-primary scale-100" : "bg-transparent scale-0")} />
                </button>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}